import { useMemo, useState, type FormEvent } from 'react'
import { Button, Switch } from '@/shared/ui'
import { minutesToTime, timeToMinutes, weekdayName } from '@/shared/lib/time'
import { inDisplayOrder } from '@/entities/working-hours'
import type { Week, WorkingDay } from '@/shared/api/types'
import { useSaveHours } from '../model'
import styles from './HoursForm.module.css'

interface HoursFormProps {
  initial: Week
}

/**
 * Часы, в которые агенту разрешено звонить.
 *
 * Время в состоянии хранится строками «09:30», а не минутами. Так поле
 * ввода остаётся управляемым во время набора: переведи мы каждое
 * нажатие в число, промежуточное «9:» стало бы невалидным, и курсор
 * прыгал бы после каждой цифры.
 */
export function HoursForm({ initial }: HoursFormProps) {
  const [days, setDays] = useState<DayDraft[]>(() => toDrafts(initial))
  const save = useSaveHours()

  const errors = useMemo(() => validate(days), [days])
  const dirty = useMemo(
    () => JSON.stringify(days) !== JSON.stringify(toDrafts(initial)),
    [days, initial],
  )

  function update(weekday: number, patch: Partial<DayDraft>) {
    setDays((prev) => prev.map((day) => (day.weekday === weekday ? { ...day, ...patch } : day)))
  }

  /** Разносит часы первого открытого дня на все остальные открытые. */
  function copyToAll() {
    const source = days.find((day) => !day.closed)
    if (!source) return
    setDays((prev) =>
      prev.map((day) => (day.closed ? day : { ...day, open: source.open, close: source.close })),
    )
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (errors.length > 0) return

    save.mutate(
      days.map<WorkingDay>((day) => ({
        weekday: day.weekday,
        openMin: timeToMinutes(day.open) ?? 0,
        closeMin: timeToMinutes(day.close) ?? 0,
        closed: day.closed,
      })),
    )
  }

  /** Ставит один и тот же режим на набор дней, остальные закрывает. */
  function preset(open: number[], from: string, to: string) {
    setDays((prev) =>
      prev.map((day) => ({
        ...day,
        closed: !open.includes(day.weekday),
        open: from,
        close: to,
      })),
    )
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.presets}>
        <span className={styles.presetsLabel}>Быстро:</span>
        <button type="button" className={styles.preset} onClick={() => preset([1, 2, 3, 4, 5], '09:00', '18:00')}>
          Будни 9–18
        </button>
        <button type="button" className={styles.preset} onClick={() => preset([1, 2, 3, 4, 5], '10:00', '20:00')}>
          Будни 10–20
        </button>
        <button
          type="button"
          className={styles.preset}
          onClick={() => preset([0, 1, 2, 3, 4, 5, 6], '10:00', '19:00')}
        >
          Ежедневно 10–19
        </button>
      </div>

      {days.map((day) => {
        const invalid = errors.some((e) => e.weekday === day.weekday)
        return (
          <div
            key={day.weekday}
            className={`${styles.day} ${day.closed ? styles.closedDay : ''}`}
          >
            <Switch
              checked={!day.closed}
              onChange={(e) => update(day.weekday, { closed: !e.target.checked })}
              label={weekdayName(day.weekday)}
            />

            {day.closed ? (
              <span className={styles.closedLabel}>Не звоним</span>
            ) : (
              <div className={styles.times}>
                <input
                  type="time"
                  className={`${styles.time} ${invalid ? styles.timeInvalid : ''}`}
                  value={day.open}
                  onChange={(e) => update(day.weekday, { open: e.target.value })}
                  aria-label={`${weekdayName(day.weekday)}: начало`}
                />
                <span className={styles.dash}>—</span>
                <input
                  type="time"
                  className={`${styles.time} ${invalid ? styles.timeInvalid : ''}`}
                  value={day.close}
                  onChange={(e) => update(day.weekday, { close: e.target.value })}
                  aria-label={`${weekdayName(day.weekday)}: конец`}
                />
              </div>
            )}

            <span className={styles.length}>{dayLength(day)}</span>
          </div>
        )
      })}

      {errors.length > 0 && (
        <div className={styles.error} role="alert">
          {errors[0]?.message}
        </div>
      )}

      <div className={styles.actions}>
        <Button type="submit" loading={save.isPending} disabled={!dirty || errors.length > 0}>
          Сохранить
        </Button>
        <button type="button" className={styles.copyAll} onClick={copyToAll}>
          Одинаковые часы во все дни
        </button>
      </div>
    </form>
  )
}

interface DayDraft {
  weekday: number
  open: string
  close: string
  closed: boolean
}

/** Сколько часов длится рабочий день. */
function dayLength(day: DayDraft): string {
  if (day.closed) return ''

  const open = timeToMinutes(day.open)
  const close = timeToMinutes(day.close)
  if (open === null || close === null || close <= open) return ''

  const hours = (close - open) / 60
  return `${hours % 1 === 0 ? hours : hours.toFixed(1)} ч`
}

function toDrafts(week: Week): DayDraft[] {
  return inDisplayOrder(week).map((day) => ({
    weekday: day.weekday,
    open: minutesToTime(day.openMin),
    close: minutesToTime(day.closeMin),
    closed: day.closed,
  }))
}

interface DayError {
  weekday: number
  message: string
}

/**
 * Проверяет то же, что и сервер, — но до отправки.
 *
 * Главное здесь не формат, а порядок: «с 18:00 до 09:00» проходит любую
 * проверку формата и означает, что агент не позвонит никогда. Молча
 * сохранить это значит отдать человеку сломанное расписание без единого
 * сообщения об ошибке.
 */
function validate(days: DayDraft[]): DayError[] {
  const errors: DayError[] = []

  for (const day of days) {
    if (day.closed) continue

    const open = timeToMinutes(day.open)
    const close = timeToMinutes(day.close)

    if (open === null || close === null) {
      errors.push({ weekday: day.weekday, message: `${weekdayName(day.weekday)}: время неверное` })
      continue
    }
    if (close <= open) {
      errors.push({
        weekday: day.weekday,
        message: `${weekdayName(day.weekday)}: конец должен быть позже начала`,
      })
    }
  }

  return errors
}
